import IBAN from 'iban';
import _ from "lodash";
import PaymentMethod from "./PaymentMethod";

export default function(ngModule) {
    ngModule.factory('Company', function($translate, BaseClass,$q, $http, $injector, Shop, Currencies, MetaData, $state, PaymentMethod) {

        /**
         * Constructor function
         *
         * @param {Object} attributes Initial attributes for the model
         */
        function Company(attributes) {
            _.assign(this,attributes);
        }
        Company.modelName = 'Company';

        BaseClass.apply(Company);

        Company.validates({
            name: {
                required: true
            },
            currency: {
                required: true,
                inclusion: {
                    in: _.map(Currencies, 'code')
                }
            },
            locale: {
                required: true,
                format: {
                    regex: {
                        regex: /^[a-z]{2,3}(_(Cyrl|Latn|Cans|Mong))?_([A-Z]{2})$/,
                        message: 'Must be a valid locale'
                    }
                }
            },
            website: {
                format: {
                    regex: {
                        regex: /^https?:\/\/(?:[a-z0-9]+\.)+[a-z]+.*/i,
                        message: 'Must be a valid URL'
                    }
                }
            },
            favicon_url: {
                format: {
                    regex: {
                        regex: /^https?:\/\/(?:[a-z0-9]+\.)+[a-z]+.*/i,
                        message: 'Must be a valid URL'
                    }
                }
            },
            transaction_fee: {
                numericality: true
            },
            product_fee_distribution: {
                numericality: true
            },
            product_fee: {
                numericality: true
            },
            bank_account_number: {
                iban: {
                    validator: function(value, instance, fieldName) {
                        if(!value || !value.length)
                            return true;
                        value = instance[fieldName] = value.toUpperCase().replace(/[^A-Z0-9]+/g, '');
                        return (value === IBAN.electronicFormat(value) && IBAN.isValid(value));
                    },
                    message: 'must be a valid IBAN'
                }
            },
            payout_frequency: {
                required: true,
                inclusion: {
                    in: ['disabled', 'manual', 'yearly', 'monthly', 'weekly', 'threshold']
                }
            },
            payout_threshold: {
                positiveInteger: {
                    message: 'Not a valid threshold (should be a positive number)',
                    validator: function(value, instance, field) {
                        if (_.get(instance, 'payout_frequency') !== 'threshold')
                            return true;

                        if (!_.isNumber(value))
                            return false;

                        return value >= 0;
                    }
                }
            },
            payout_reference: {
                format: {
                    regex: {
                        regex: /^[A-Za-z0-9\-_ ]{0,16}$/,
                        message: 'The payout reference may only contain letters, numbers, dashes and spaces, with a maximum length of 16 characters'
                    }
                }
            },
            transaction_percentage: {
                numericality: true
            }
        });

        Company.configureApi('/company/:guid');
        Company.prototype.toString = function() { return this.name; };

        Company.getMe = function() {
            let adminBrowser = $state.transition && $state.transition.isActive() ? $state.transition.params().as : $state.params.as;
            if(adminBrowser)
                return Company.get({ guid: adminBrowser });
            if(Company.cached.me)
                return $q.resolve(Company.cached.me);
            return Company.get({ guid: 'me' })
                .then(company => {
                    Company.cached.me = company;
                    return company;
                });
        };

        Company.getCurrentId = function(){
            let companyId = _.get($state.params, 'as');

            if (!companyId || !_.has(Company.cached, companyId))
                companyId = 'me';

            return companyId;
        }

        /**
         * Undelete a Company
         *
         * @return {HTTPResponse} Promise
         */
        Company.prototype.$reinstate = ()=> $http.post(`/whitelabel/${this.guid}/reinstate`);

        Company.belongsToMany('PaymentMethod','paymentmethod','/paymentmethod/:guid/^guid',[
            {
                type: 'attach',
                fn: function(modelName, url, instance, child, pivot){
                    return $http.put(url, pivot).then( response => {
                        instance.broadcastAPIMessage(response);
                        instance.belongsToMany.cache(modelName, child, pivot);
                        if(child.belongsToMany[instance.constructor.modelName])
                            child.belongsToMany.cache(instance.constructor.modelName, instance, pivot);
                        return $q.resolve(instance);
                    }, error => {
                        instance.broadcastAPIMessage(error);
                        return $q.reject(error);
                    });

                }
            },
            { type: 'detach' }
        ]);

        /**
         * Find this company's global shop
         *
         * @return {Promise<Shop>} Finds or creates the companies global shop
         */
        Company.prototype.$getOrCreateShop = function() {
            let RelationAPI = $injector.get('RelationAPI');
            let company = this;
            return RelationAPI.get('CompanyShop', this.guid).then(relations => {
                if(relations.length)
                    return Shop.get({ guid: relations[0].right });
                let shop = Shop.new({
                    name: $translate.instant('models.company.shop'),
                });
                return shop.$save()
                    .then(() => RelationAPI.create('CompanyShop', company.guid, shop.guid))
                    .then(() => MetaData.query())
                    .then(meta => {
                        var defaults = _.filter(meta, m => {
                            return m.public && /^(age|city|gender)$/.test(m.name);
                        });
                        return _.reduce(
                            defaults,
                            (promise, meta) => promise.then(() => shop.$attachMetaData(meta)),
                            $q.resolve()
                        );
                    })
                    .then(() => shop);
            });
        };

        /**
         * Update a company's global shop
         *
         * @param {String} shopGuid The new global shop guid
         * @return {Promise} Resolves when done
         */
        Company.prototype.$updateCompanyShop = function(shopGuid) {
            let RelationAPI = $injector.get('RelationAPI');
            let company = this;
            return RelationAPI.delete('CompanyShop', company.guid)
                .then(() => RelationAPI.create('CompanyShop', company.guid, shopGuid));
        };

        /**
         * Find this company's import shop
         *
         * @return {Promise<Shop>} Finds or creates the companies import shop
         */
        Company.prototype.$getOrCreateImportShop = function() {
            let RelationAPI = $injector.get('RelationAPI');
            let company = this;
            return RelationAPI.get('ImportShop', this.guid).then(relations => {
                if(relations.length)
                    return Shop.get({ guid: relations[0].right });
                let shop = Shop.new({
                    name: $translate.instant('models.company.importShop'),
                });
                return shop.$save()
                    .then(() => RelationAPI.create('ImportShop', company.guid, shop.guid))
                    .then(() => shop);
            });
        };

        /**
         * Find this company's offline shop
         *
         * @return {Promise<Shop>} Finds or creates the companies offline shop
         */
        Company.prototype.$getOrCreateOfflineShop = function() {
            let RelationAPI = $injector.get('RelationAPI');
            let company = this;
            return RelationAPI.get('OfflineShop', this.guid).then(relations => {
                if(relations.length)
                    return Shop.get({ guid: relations[0].right });
                let shop = Shop.new({
                    name: $translate.instant('models.company.offlineShop'),
                });
                return shop.$save()
                    .then(() => RelationAPI.create('OfflineShop', company.guid, shop.guid))
                    .then(() => shop);
            });
        };

        Company.prototype.getCashrSalesAccounts = function() {
            return $http.get(`/company/${this.guid}/cashr/accounts`).then(res => {
                if (res.data.length < 1)
                    return null;
                return  res.data.value;
            });
        };

        Company.prototype.getCashrVats = function() {
            return $http.get(`/company/${this.guid}/cashr/vats`).then(res => {
                if (res.data.length < 1)
                    return null;
                return  res.data.value;
            });
        };

        Company.prototype.getCashrTypes = function() {
            return $http.get(`/company/${this.guid}/cashr/types`).then(res => {
                if (res.data.length < 1)
                    return null;
                return  res.data.value;
            });
        };

        /**
         * Query the event for Tickets WITH nested shops
         *
         * @returns {Promise<Array>} Resolves with an array of methods
         */
        Company.prototype.$queryPaymentMethodsWithShops = function(currency) {
            return $http.get(`/paymentmethod/withshops/${ this.guid }/${currency}`).then(response => {
                this.broadcastAPIMessage(response);

                return $q.resolve(_.map(response.data, methodData => {
                    let method = PaymentMethod.new(methodData);

                    method.shops = _.map(methodData.used_by_shops, shopData => Shop.new(shopData));

                    return method;
                }));
            }, error => {
                this.broadcastAPIMessage(error);

                return $q.reject(error);
            });
        };

        return Company;
    });
}
